import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, LinearProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Dashboard, Receipt, Description, Build, EventRepeat } from '@mui/icons-material';
import axios from '../../api/axios';
import { useApiRequest } from '../../api/axios';
import { useAnalytics } from '../../api/analytics';
import { SERVICE_REMINDERS_SUMMARY_ENDPOINT } from '../../consts';
import { useHasPermission } from '../../contexts/auth';
import { ServiceReminderSummary } from '../../types/serviceReminders';
import { AnalyticsData } from '../../types/analytics';
import { formatCompletionPercent } from '../../utils';
import { Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';

export const StatCard: React.FC<{
  title: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  color?: string;
  onClick?: () => void;
  minWidth?: number;
  /** Tighter padding, value and icon - for screens (the dashboard) that show
   *  many cards at once and should fit without scrolling. */
  dense?: boolean;
}> = ({
  title,
  value,
  icon,
  color = '#1976d2',
  onClick,
  minWidth = 250,
  dense = false,
}) => {
  const isSimpleValue = typeof value === 'string' || typeof value === 'number';

  return (
    <Card
      onClick={onClick}
      sx={{
        flex: `1 1 ${minWidth}px`,
        minWidth: `${minWidth}px`,
        borderRadius: 'var(--defaultRadius)',
        boxShadow: 3,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.1s ease-in-out',
        '&:hover': {
          transform: onClick ? 'translateY(-2px)' : 'none'
        }
      }}
    >
      <CardContent sx={dense ? { py: 1.5, px: 2, '&:last-child': { pb: 1.5 } } : undefined}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom={!dense}>
              {title}
            </Typography>
            {isSimpleValue ? (
              <Typography variant={dense ? 'h5' : 'h4'} component="div" fontWeight="bold">
                {value}
              </Typography>
            ) : (
              value
            )}
          </Box>
          <Box sx={{ color, fontSize: dense ? 36 : 48, opacity: 0.7, display: 'flex' }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const DashboardComponent: React.FC = () => {
  const { t } = useTranslation();
  const { loading, getAnalytics } = useAnalytics();
  const { executeRequest } = useApiRequest();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [serviceReminders, setServiceReminders] = useState<ServiceReminderSummary | null>(null);
  const canViewServiceReminders = useHasPermission('reminders.view_reminder');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAnalytics = async () => {
      const data = await getAnalytics();
      if (data) {
        setAnalyticsData(data);
      }
    };

    fetchAnalytics();
  }, [getAnalytics]);

  useEffect(() => {
    if (!canViewServiceReminders) return;

    // Its own request, not part of the analytics payload: a workspace with no
    // sale-and-service contracts never renders this card, and should not pay
    // for the aggregate behind it either.
    const fetchServiceReminders = async () => {
      const response = await executeRequest(() =>
        axios.get(SERVICE_REMINDERS_SUMMARY_ENDPOINT)
      );
      if (response?.data) {
        setServiceReminders(response.data);
      }
    };

    fetchServiceReminders();
  }, [canViewServiceReminders, executeRequest]);

  if (loading) {
    const StatCardSkeleton = ({ minWidth = 250 }: { minWidth?: number }) => (
      <Card sx={{ flex: `1 1 ${minWidth}px`, minWidth: `${minWidth}px`, borderRadius: 'var(--defaultRadius)', boxShadow: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Skeleton variant="text" width={120} height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={80} height={36} />
            </Box>
            <Skeleton variant="circular" width={48} height={48} />
          </Box>
        </CardContent>
      </Card>
    );

    return (
      <Box sx={{ width: '100%' }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Skeleton variant="circular" width={32} height={32} sx={{ mr: 1 }} />
          <Skeleton variant="text" width={180} height={40} />
        </Box>

        {/* Jobs Section */}
        <Box mb={3}>
          <Skeleton variant="text" width={180} height={28} sx={{ mb: 1 }} />
          <Box display="flex" gap={2} flexWrap="wrap">
            <StatCardSkeleton minWidth={150} />
            <StatCardSkeleton minWidth={150} />
            <StatCardSkeleton minWidth={150} />
            <StatCardSkeleton minWidth={150} />
            <StatCardSkeleton minWidth={150} />
            <StatCardSkeleton minWidth={150} />
          </Box>
        </Box>

        {/* Settlements Section */}
        <Box mb={3}>
          <Skeleton variant="text" width={160} height={28} sx={{ mb: 1 }} />
          <Box display="flex" gap={2} flexWrap="wrap">
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </Box>
        </Box>

        {/* Contracts Section */}
        <Box mb={3}>
          <Skeleton variant="text" width={140} height={28} sx={{ mb: 1 }} />
          <Box display="flex" gap={2} flexWrap="wrap">
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </Box>
        </Box>
      </Box>
    );
  }

  if (!analyticsData) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="text.secondary">
          {t('No analytics data available')}
        </Typography>
      </Box>
    );
  }

  const allCurrentMonth = analyticsData.settlements.all_current_month || 0;
  const readyToSettleCurrentMonth = analyticsData.settlements.ready_to_settle_current_month || 0;
  const confirmedCurrentMonth = analyticsData.settlements.confirmed_current_month || 0;
  const readyToSettleProgress =
    allCurrentMonth > 0 ? (readyToSettleCurrentMonth / allCurrentMonth) * 100 : 0;
  const readyToSettleProgressClamped = Math.max(0, Math.min(100, readyToSettleProgress));
  const settledProgress =
    readyToSettleCurrentMonth > 0 ? (confirmedCurrentMonth / readyToSettleCurrentMonth) * 100 : 0;
  const settledProgressClamped = Math.max(0, Math.min(100, settledProgress));

  const complaintsNotFinished = analyticsData.complaints_not_finished_count || 0;

  // The workspace box already pads its content and has a fixed height, so the
  // dashboard adds no padding of its own and keeps its rows tight enough to
  // fit without a scrollbar.
  const sectionTitleSx = { mb: 1 };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box display="flex" alignItems="center">
        <Dashboard sx={{ fontSize: 28, mr: 1 }} />
        <Typography variant="h5" component="h1">
          Dashboard
        </Typography>
      </Box>

      {/* Jobs Section */}
      <Box data-tour="dashboard-jobs">
        <Typography variant="h6" sx={sectionTitleSx}>
          {t('Service')}
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2}>
          <StatCard
            title={t('Pending')}
            value={analyticsData.jobs_by_status.P}
            icon={<Build />}
            color="#ed6c02"
            onClick={() => navigate('/service/jobs?status=P')}
            minWidth={150}
            dense
          />
          <StatCard
            title={t('In Progress')}
            value={analyticsData.jobs_by_status.IP}
            icon={<Build />}
            color="#9e9e9e"
            onClick={() => navigate('/service/jobs?status=IP')}
            minWidth={150}
            dense
          />
          <StatCard
            title={t('For Verification')}
            value={analyticsData.jobs_by_status.FV}
            icon={<Build />}
            color="#0288d1"
            onClick={() => navigate('/service/jobs?status=FV')}
            minWidth={150}
            dense
          />
          <StatCard
            title={t('For Improvement')}
            value={analyticsData.jobs_by_status.FI}
            icon={<Build />}
            color="#f57c00"
            onClick={() => navigate('/service/jobs?status=FI')}
            minWidth={150}
            dense
          />
          <StatCard
            title={t('Complaints')}
            value={complaintsNotFinished}
            icon={<Build />}
            color="#d32f2f"
            onClick={() => navigate('/service/jobs?kind=C')}
            minWidth={150}
            dense
          />
          <StatCard
            title={t('Finished')}
            value={analyticsData.jobs_by_status.F}
            icon={<Build />}
            color="#2e7d32"
            onClick={() => navigate('/service/jobs?status=F')}
            minWidth={150}
            dense
          />
        </Box>
      </Box>

      {/* Settlements Section */}
      <Box data-tour="dashboard-settlements">
        <Typography variant="h6" sx={sectionTitleSx}>
          {t('Settlements')}
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <StatCard
            title={t('Overdue Past Months')}
            value={analyticsData.settlements.overdue_past_months}
            icon={<Receipt />}
            color="#d32f2f"
            onClick={() => navigate('/settlements')}
            dense
          />
          <StatCard
            title={t('Ready to Settle / Current Month')}
            value={(
              <Box sx={{ minWidth: 170 }}>
                <Typography variant="h5" component="div" fontWeight="bold">
                  {`${readyToSettleCurrentMonth}/${allCurrentMonth}`}
                </Typography>
                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={readyToSettleProgressClamped}
                    sx={{ flex: 1, height: 8, borderRadius: 999 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {`${formatCompletionPercent(readyToSettleProgressClamped)}%`}
                  </Typography>
                </Box>
              </Box>
            )}
            icon={<Receipt />}
            color="#0288d1"
            onClick={() => navigate('/settlements')}
            dense
          />
          <StatCard
            title={t('Settled / Ready to Settle')}
            value={(
              <Box sx={{ minWidth: 170 }}>
                <Typography variant="h5" component="div" fontWeight="bold">
                  {`${confirmedCurrentMonth}/${readyToSettleCurrentMonth}`}
                </Typography>
                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={settledProgressClamped}
                    sx={{ flex: 1, height: 8, borderRadius: 999 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {`${formatCompletionPercent(settledProgressClamped)}%`}
                  </Typography>
                </Box>
              </Box>
            )}
            icon={<Receipt />}
            color="#2e7d32"
            onClick={() => navigate('/settlements')}
            dense
          />
        </Box>
      </Box>

      {/* Contracts and obligatory services share a row (three cards and two),
          so side by side they take one row instead of two. */}
      <Box display="flex" gap={2} flexWrap="wrap">
        <Box sx={{ flex: '3 1 540px', minWidth: 0 }}>
          <Typography variant="h6" sx={sectionTitleSx}>
            {t('Contracts')}
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <StatCard
              title={t('Active')}
              value={analyticsData.contracts_active}
              icon={<Description />}
              color="#2e7d32"
              onClick={() => navigate('/contracts?ended=false&ordering=end_date')}
              minWidth={160}
              dense
            />
            <StatCard
              title={t('Expiring Soon')}
              value={analyticsData.contracts_expiring_soon}
              icon={<Description />}
              color="#ed6c02"
              onClick={() => navigate('/contracts?ordering=-end_date')}
              minWidth={160}
              dense
            />
            <StatCard
              title={t('Ended')}
              value={analyticsData.contracts_ended}
              icon={<Description />}
              color="#9e9e9e"
              onClick={() => navigate('/contracts?ended=true')}
              minWidth={160}
              dense
            />
          </Box>
        </Box>

        {/* Obligatory services - only where a workspace actually has some */}
        {serviceReminders && serviceReminders.total > 0 && (
          <Box sx={{ flex: '2 1 380px', minWidth: 0 }}>
            <Typography variant="h6" sx={sectionTitleSx}>
              {t('Obligatory services')}
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <StatCard
                title={t('Overdue')}
                value={serviceReminders.overdue}
                icon={<EventRepeat />}
                color="#d32f2f"
                onClick={() => navigate('/service/upcoming-services?is_overdue=true')}
                minWidth={180}
                dense
              />
              <StatCard
                title={t('Due in the next {{count}} days', {
                  count: serviceReminders.window_days,
                })}
                value={serviceReminders.due_soon}
                icon={<EventRepeat />}
                color="#ed6c02"
                onClick={() => navigate('/service/upcoming-services')}
                minWidth={180}
                dense
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
